import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { Table, Input, Typography, Spin, Select, notification } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Category, RowDataType, UsageResponse } from '@app/models/feed_usage/FeedUsageResponse';
import { createUsage, editUsage, showUsage, storeUsage, updateUsage } from '@app/api/feedMaterialUsage.api';
import { UsageRequest } from '@app/models/feed_usage/FeedUsageRequest';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';

interface FeedUsageProps {
  id?: string;
  type: 'edit' | 'create' | 'view';
  pageTitle: string;
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const { Title } = Typography;
const { Option } = Select;

interface CountryOption {
  id: number;
  name: string;
}

const FeedUsageComponent: React.FC<FeedUsageProps> = ({ type, pageTitle, editable, id }) => {
  const [feedTypes, setFeedTypes] = useState<Category[]>([]);
  const [feedValues, setFeedValues] = useState<{
    [key: number]: {
      local_qty?: string;
      local_price?: string;
      imported_qty?: string;
      imported_price?: string;
      country_ids?: number[];
    };
  }>({});
  const [changedValues, setChangedValues] = useState<{
    [key: number]: {
      local_qty?: string;
      local_price?: string;
      imported_qty?: string;
      imported_price?: string;
      country?: string;
      country_ids?: number[];
    };
  }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [form] = Form.useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [countries, setCountries] = useState<{ value: number; label: string }[]>([]);

  interface FeedValue {
    local_qty?: string;
    local_price?: string;
    imported_qty?: string;
    imported_price?: string;
    country_ids?: number[];
  }

  const years = Array.from({ length: 26 }, (_, i) => new Date().getFullYear() - i);

  const fetchFeeds = useCallback(async () => {
    setLoading(true);
    try {
      let data: UsageResponse;
      switch (type) {
        case 'create':
          data = await createUsage();
          if (data.pr_feed_types) {
            setFeedTypes(data.pr_feed_types);
          }
          if (data.country_options) {
            setCountries(data.country_options);
          }
          break;
        case 'edit':
        case 'view':
          if (id) {
            data = type === 'edit' ? await editUsage(parseInt(id)) : await showUsage(parseInt(id));
            console.log(`${type} mode data:`, data);

            if (data.pr_feed_types) {
              setFeedTypes(data.pr_feed_types);
            } else if (data.feed_types) {
              setFeedTypes(data.feed_types);
            }
            if (data.country_options) {
              setCountries(data.country_options);
            }

            // Try to find usage lines in different possible locations
            const usageLines =
              data.feed_usage?.pr_feed_usage_lines ||
              data.pr_feed_expected_ingredient?.pr_feed_ingredient_usage_lines ||
              [];

            if (usageLines.length > 0) {
              const existingValues: {
                [key: number]: FeedValue;
              } = {};

              usageLines.forEach((line: any) => {
                let countryIds: number[] = [];

                if (line.countries && Array.isArray(line.countries)) {
                  countryIds = line.countries.map((country: any) =>
                    typeof country === 'object' ? country.id : country,
                  );
                } else if (line.country_ids && Array.isArray(line.country_ids)) {
                  countryIds = line.country_ids;
                }

                const feedTypeId = Number(line.pr_feed_type_id);

                existingValues[feedTypeId] = {
                  local_qty: String(line.local_qty || '0'),
                  local_price: String(line.local_unit_price || line.local_price || '0'),
                  imported_qty: String(line.imported_qty || '0'),
                  imported_price: String(line.imported_unit_price || line.imported_price || '0'),
                  country_ids: countryIds,
                };
              });
              setFeedValues(existingValues);
            } else {
              console.warn('No usage lines found in the response');
            }

            const year = data.feed_usage?.year || data.year;
            if (year) {
              setSelectedYear(parseInt(year.toString()));
            }
          }
          break;
      }
    } catch (error) {
      console.error(`Error fetching feeds for ${type} mode:`, error);
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  const handleValueChange = (id: number, field: string, value: any) => {
    setFeedValues((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));

    setChangedValues((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={loading} onClick={handleSubmit} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/feed-material-usage')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-feed-usage`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, loading, editable, id, addHeaderButton, t, navigate, removeHeaderButton, changedValues]);

  const filterData = (categories: Category[]) => {
    if (!searchTerm) return categories;

    return categories
      .map((category) => {
        // Check if the parent category (Raw Material) matches the search term
        if (category.type.toLowerCase().includes(searchTerm.toLowerCase())) {
          return category;
        }

        // Filter parent layers based on the search term
        const filteredParents = category.children
          ?.map((parent) => {
            if (parent.type.toLowerCase().includes(searchTerm.toLowerCase())) {
              return parent;
            }

            // Filter child layers within the parent based on the search term
            const filteredChildren = parent.children?.filter((child) =>
              child.type?.toLowerCase().includes(searchTerm.toLowerCase()),
            );

            return filteredChildren && filteredChildren.length > 0 ? { ...parent, children: filteredChildren } : null;
          })
          .filter(Boolean);

        return filteredParents && filteredParents.length > 0 ? { ...category, children: filteredParents } : null;
      })
      .filter(Boolean) as Category[];
  };

  type FeedValuesMap = Record<number, FeedValue>;

  // Use a more specific type for the field parameter
  type InputFieldKey = 'local_qty' | 'local_price' | 'imported_qty' | 'imported_price';

  const transformData = (categories: Category[]) => {
    const filteredCategories = filterData(categories);
    const rows: RowDataType[] = [];

    filteredCategories.forEach((category) => {
      if (category.type.trim() === 'Raw Material') {
        category.children?.forEach((subCategory) => {
          // Create parent row
          rows.push(createRow(subCategory, true));

          // Create children rows
          subCategory.children?.forEach((child) => {
            rows.push(createRow(child, false));
          });
        });
      }
    });
    return rows;
  };

  // Helper function to create a row with properly typed parameters
  const createRow = (item: { id: number; type?: string }, isParent = false) => {
    return {
      key: `${isParent ? 'parent' : 'child'}-${item.id}`,
      category: item.type || '',
      isParent: isParent,
      feedTypeId: item.id,
      local_qty: createInput(item.id, 'local_qty'),
      local_price: createInput(item.id, 'local_price'),
      imported_qty: createInput(item.id, 'imported_qty'),
      imported_price: createInput(item.id, 'imported_price'),
      country: createCountrySelect(item.id),
    };
  };

  const createInput = (id: number, field: InputFieldKey) => {
    const value = feedValues[id]?.[field] ?? '';

    return (
      <BaseInput
        type="number"
        value={value}
        onChange={(e) => handleValueChange(id, field, e.target.value)}
        style={{ width: '100%' }}
        disabled={!editable}
      />
    );
  };

  const createCountrySelect = (id: number) => {
    const selectedCountryIds = feedValues[id]?.country_ids || [];
    return (
      <Select
        mode="multiple"
        style={{ width: '100%' }}
        value={selectedCountryIds}
        onChange={(value) => handleValueChange(id, 'country_ids', value)}
        placeholder="Select countries"
        disabled={!editable}
        showSearch
        optionFilterProp="label"
      >
        {countries.map((country) => (
          <Option key={country.value} value={country.value}>
            {country.label}
          </Option>
        ))}
      </Select>
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const lines = Object.entries(changedValues).map(([feedTypeId, values]) => ({
        pr_feed_type_id: parseInt(feedTypeId),
        local_qty: parseFloat(values.local_qty || '0'),
        local_unit_price: parseFloat(values.local_price || '0'),
        imported_qty: parseFloat(values.imported_qty || '0'),
        imported_unit_price: parseFloat(values.imported_price || '0'),
        country_ids: values.country_ids || [],
      }));

      const payload: UsageRequest = {
        year: selectedYear.toString(),
        pr_feed_ingredient_usage_lines: lines,
      };

      if (type === 'create') {
        await storeUsage(payload);
      } else if (type === 'edit' && id) {
        await updateUsage(parseInt(id), payload);
      }

      navigate('/feed-material-usage');
    } catch (error) {
      console.error('Error submitting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Raw Material',
      dataIndex: 'category',
      key: 'category',
      render: (text: string, record: RowDataType) => (record.isParent ? <strong>{text}</strong> : text),
      width: 220,
    },
    {
      title: 'Locally Purchased',
      children: [
        {
          title: 'Quantity (MT)',
          dataIndex: 'local_qty',
          key: 'local_qty',
          width: 120,
        },
        {
          title: 'Mean Price (Rs/MT)',
          dataIndex: 'local_price',
          key: 'local_price',
          width: 150,
        },
      ],
    },
    {
      title: 'Imported',
      children: [
        {
          title: 'Quantity (MT)',
          dataIndex: 'imported_qty',
          key: 'imported_qty',
          width: 120,
        },
        {
          title: 'Mean Price (Rs/MT)',
          dataIndex: 'imported_price',
          key: 'imported_price',
          width: 150,
        },
        {
          title: 'Names of the Country Imported',
          dataIndex: 'country',
          key: 'country',
          width: 200,
        },
      ],
    },
  ];

  return (
    <BaseCard>
      <Title level={3}>{pageTitle || 'Feed Material Usage'}</Title>
      <div style={{ display: 'flex', marginBottom: '16px', gap: '10px' }}>
        <BaseInput
          prefix={<SearchOutlined />}
          placeholder="Search feed types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <Select
          style={{ width: '120px' }}
          value={selectedYear}
          onChange={(value) => setSelectedYear(value)}
          disabled={type !== 'create'}
        >
          {years.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </div>
      <Form form={form} onFinish={handleSubmit}>
        {loading ? (
          <Spin size="small" />
        ) : (
          <Table
            columns={columns}
            dataSource={transformData(feedTypes)}
            pagination={false}
            bordered
            size="small"
            locale={{ emptyText: 'No data available' }}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Form>
    </BaseCard>
  );
};

export default FeedUsageComponent;
